import React, { useState, useRef, useEffect } from "react";
import "../styles/LevelUpEngagementStyles.css";
import PlayIcon from "../assets/playIcon.png";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import MessageIcon from "../assets/messageIcon.png";
import TestPadIcon from "../assets/testpadIcon.png";
import SessionIcon from "../assets/sessionIcon.png";

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

const LevelUpEngagementComponent: React.FC = () => {
  // Course start date - Jan 25th, 2026
  const courseStartDate = new Date(2026, 0, 25);
  const today = new Date();

  const [currentDate, setCurrentDate] = useState<Date>(today);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [availableMonths, setAvailableMonths] = useState<
    Array<{
      year: number;
      month: number;
      label: string;
      value: string;
      isDisabled: boolean;
    }>
  >([]);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownListRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);

  const cardDetails = [
    {
      title: "Master the Assessments",
      desc: "Participate in quizzes to show true dedication.",
      button: "Take Assessment",
      bg: "#FFF6D8",
      icon: TestPadIcon,
    },
    {
      title: "Conquer the Video Library",
      desc: "Aim for 100% completion on a module this week.",
      button: "Go to Program Hub",
      bg: "#DFF2FF",
      icon: PlayIcon,
    },
    {
      title: "Become a Forum Sage",
      desc: "Share wisdom or ask a great question in the forums.",
      button: "Add Query",
      bg: "#D2FCFB",
      icon: MessageIcon,
    },
    {
      title: "Ace Your Attendance",
      desc: "Join live sessions to connect with instructors.",
      button: "Join Live Sessions",
      bg: "#F8EAFF",
      icon: SessionIcon,
    },
  ];

  const highlightedDates = [26, 27, 28];

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Generate available months from course start date to current date
  const generateAvailableMonths = () => {
    const options = [];
    const startYear = courseStartDate.getFullYear();
    const startMonth = courseStartDate.getMonth();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    // Loop from course start date to current date
    for (let year = startYear; year <= currentYear; year++) {
      const startMonthForYear = year === startYear ? startMonth : 0;
      const endMonthForYear = year === currentYear ? currentMonth : 11;

      for (let month = startMonthForYear; month <= endMonthForYear; month++) {
        options.push({
          year,
          month,
          label: `${months[month]}'${year.toString().slice(-2)}`,
          value: `${year}-${month}`,
          isDisabled: false,
        });
      }
    }

    return options;
  };

  // Initialize available months
  useEffect(() => {
    setAvailableMonths(generateAvailableMonths());
  }, []);

  // Check if user is currently on current month/year
  const isCurrentMonthYear = () => {
    return (
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  // Check if the selected date is within valid range (after course start date)
  const isDateValid = (date: Date) => {
    // Disable dates before course start date
    if (date < courseStartDate) {
      return false;
    }
    // Disable future dates
    if (date > today) {
      return false;
    }
    return true;
  };

  // Format current date for display (e.g., "March'26")
  const formatCurrentMonthYear = () => {
    return `${months[currentDate.getMonth()]}'${currentDate.getFullYear().toString().slice(-2)}`;
  };

  // Scroll to selected item when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && dropdownListRef.current && selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [isDropdownOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleMonthYearSelect = (year: number, month: number) => {
    // Keep the same day, but change month/year
    const newDate = new Date(year, month, currentDate.getDate());
    // If the date is invalid (like Jan 31 -> Feb 31 becomes March 3), adjust to last day of month
    if (newDate.getMonth() !== month) {
      newDate.setDate(0); // Set to last day of previous month
    }
    setCurrentDate(newDate);
    setIsDropdownOpen(false);
  };

  // Handle calendar date change - DISABLED - do nothing
  const handleDateChange = (value: Value) => {
    // Do nothing - dates are not clickable
    return;
  };

  // Handle Today button click
  const handleTodayClick = () => {
    setCurrentDate(new Date());
  };

  // Check if a date is today
  const isToday = (date: Date) => {
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Disable ALL tiles - make them non-clickable
  const disableTile = ({ date, view }: { date: Date; view: string }) => {
    // Disable all tiles in month view
    if (view === "month") {
      return true; // All tiles are disabled
    }
    return false;
  };

  // Custom tile class name
  const getTileClassName = ({ date, view }: { date: Date; view: string }) => {
    if (view === "month") {
      const day = date.getDate();

      // Check if date is before course start or future
      if (!isDateValid(date)) {
        return "v2-levelDisabledDay";
      }

      // Check if it's a highlighted date (active days)
      if (highlightedDates.includes(day)) {
        return "v2-levelActiveDay";
      }

      // Check if it's today's date - only add dot, no background change
      if (isToday(date)) {
        return "v2-levelTodayDate";
      }

      return "v2-levelInactiveDay";
    }
    return "";
  };

  return (
    <div className="v2-levelEngagementContent">
      <div className="v2-levelEngagementLeftContent">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginLeft: "5px",
          }}
        >
          <div
            style={{
              fontFamily: "Mazzard",
              fontSize: "18px",
              color: "#353639",
              fontWeight: "600",
            }}
          >
            Engagement
            <div
              style={{
                fontFamily: "Mazzard",
                fontSize: "13px",
                color: "#353639",
                fontWeight: "300",
                marginBottom: "12px",
              }}
            >
              Monthly Activity Pulse
            </div>
          </div>

          {/* Single Custom Dropdown and Today Button Container */}
          <div
            style={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              marginBottom: "20px",
              gap: "12px",
            }}
          >
            {/* Today Button - Show only if not on current month/year */}
            {!isCurrentMonthYear() && (
              <button onClick={handleTodayClick} className="v2-levelTodayBtn">
                <div className="v2-todayDot">{""}</div>
                Today
              </button>
            )}
            {/* Custom Month-Year Dropdown */}
            <div
              className="v2-levelCustomDropdown"
              ref={dropdownRef}
              style={{
                width: "auto",
              }}
            >
              <div
                className="v2-levelDropdownHeader"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <span>{formatCurrentMonthYear()}</span>
                <svg
                  className={`v2-levelDropdownArrow ${isDropdownOpen ? "v2-levelDropdownArrowOpen" : ""}`}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 9L12 15L18 9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              {isDropdownOpen && (
                <div className="v2-levelDropdownList" ref={dropdownListRef}>
                  {availableMonths.map((option) => {
                    const isSelected =
                      currentDate.getMonth() === option.month &&
                      currentDate.getFullYear() === option.year;

                    return (
                      <div
                        key={option.value}
                        ref={isSelected ? selectedItemRef : null}
                        className={`v2-levelDropdownItem ${
                          isSelected ? "v2-levelDropdownItemActive" : ""
                        }`}
                        onClick={() =>
                          handleMonthYearSelect(option.year, option.month)
                        }
                      >
                        {option.label}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <Calendar
          view="month"
          calendarType="gregory"
          value={currentDate}
          onChange={handleDateChange}
          //   formatShortWeekday={(locale: any, date: { toLocaleDateString: (arg0: any, arg1: { weekday: string; }) => string | any[]; }) =>
          //     date.toLocaleDateString(locale, { weekday: "short" }).slice(0, 1)
          //   }
          formatShortWeekday={(locale, date) =>
            date.toLocaleDateString(locale, { weekday: "short" }).slice(0, 1)
          }
          tileClassName={getTileClassName}
          tileDisabled={disableTile}
          showNavigation={false}
        />
      </div>

      <div className="v2-levelEngagementRightContent">
        <div className="v2-levelCardGrid">
          {cardDetails.map((card, index) => (
            <div
              key={index}
              className="v2-levelEngagementCard"
              style={{ backgroundColor: card.bg }}
            >
              <div className="v2-levelCardTop">
                <img src={card.icon} alt="icon" className="v2-levelIcon" />

                <div>
                  <h3 className="v2-levelTitle">{card.title}</h3>
                  <p className="v2-levelDesc">{card.desc}</p>
                </div>
              </div>

              <button className="v2-levelActionBtn">{card.button}</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LevelUpEngagementComponent;
